import { graphql } from 'react-relay';
import type { FunctionComponent, RefObject } from 'react';
import isEqual from 'lodash/isEqual';
import React, { Fragment, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE,
	IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceFailure,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PopupPreview } from '@confluence/template-preview/entry-points/PopupPreview.ts';
import {
	SortByDropdown,
	SortOrders,
	LoadingSortByDropdown,
} from '@confluence/template-utils/entry-points/SortByDropdown.ts';
import { IN_EDITOR_TEMPLATES_SEGMENT_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { requireStyleClassResourcesForTemplates } from '@confluence/template-utils/entry-points/requireStyleClassResourcesForTemplates.ts';
import {
	getCountedCategories,
	FEATURED_CATEGORY_ID,
	MY_TEMPLATES_CATEGORY_ID,
} from '@confluence/template-utils/entry-points/categoryUtils.ts';
import {
	filterEditorTutorialTemplate,
	filterTemplatesBySearchQuery,
	filterTemplatesByAllowList,
	updateFavouriteTemplates,
	filterTemplatesByCategoryIds,
	getFeaturedCategoryCount,
	getMyTemplatesCategoryCount,
	getExistingCategoryIds,
} from '@confluence/template-utils/entry-points/templateUtils.ts';
import type { Category as CountedCategory } from '@confluence/category-selector';
import { CategorySelector } from '@confluence/category-selector';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useIncludeEditorTutorialTemplate } from '@confluence/onboarding-helpers/entry-points/hooks/useIncludeEditorTutorialTemplate.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import {
	updateFavouriteTemplatesAllTemplatesQuery,
	mergeCurrentAndSelectedTemplates,
} from './templateListHelpers.tsx';
import type {
	TemplateListQuery as TemplateListQuery$data,
	TemplateListQueryVariables as TemplateListQuery$variables,
	TemplateListQuery_allTemplates_PaginatedTemplateInfoList_nodes_TemplateInfo as Template,
} from './__types__/TemplateListQuery.ts';
import type {
	TemplateListTemplateCategoriesQuery as TemplateListTemplateCategoriesQuery$data,
	TemplateListTemplateCategoriesQueryVariables as TemplateListTemplateCategoriesQuery$variables,
} from './__types__/TemplateListTemplateCategoriesQuery.ts';
import type { TemplateListUserQuery as TemplateListUserQuery$data } from './__types__/TemplateListUserQuery.ts';
import {
	EmptyTemplateList,
	ErrorTemplateList,
	EmptyFeaturedTemplates,
	EmptyMyTemplates,
	NoSearchResultsTemplateList,
} from './EmptyTemplateLists/index.ts';
import { TemplateListComponent } from './TemplateListComponent.tsx';
import { LoadingTemplateListCards } from './LoadingTemplateListCards.tsx';
import {
	TemplateListContainer,
	ListWrapper,
	CategoriesWrapper,
	SortByWrapper,
	CountDisplayWrapper,
} from './TemplateListStyles.tsx';
export type OnTemplateSelect = (template: Template, additionalStatAttributes: object) => void;
type TemplateListProps = {
	onTemplateSelect: OnTemplateSelect;
	spaceKey: string;
	currentSpaceKey?: string;
	searchQuery?: string;
	selectedTemplateKey?: string;
	isDisabled?: boolean;
	templatesPageSize?: number;
	pageTitle?: string;
	templateBlueprintAllowList?: string[];
	/**
	 * When true, the templates list and individual cards visually extend 16px past
	 * their normal inline edges so the hover/active highlight bleeds into the
	 * surrounding container's horizontal padding. Use this in surfaces like the
	 * in-editor templates panel where the parent has 16px of inline padding.
	 */
	hasHighlightBleed?: boolean;
};

export const i18n = defineMessages({
	allCategoryName: {
		id: 'template-list.categories.all.category.name',
		defaultMessage: 'All',
		description:
			'The text is shown as a category filter button label in the template list. When selected, it displays all templates across every category without filtering.',
	},
	featuredCategoryName: {
		id: 'template-list.categories.featured.category.name',
		defaultMessage: 'Featured',
		description: 'Text for the name of `Featured` category',
	},
	myTemplatesCategoryName: {
		id: 'template-list.categories.my-templates.category.name',
		defaultMessage: 'My Templates',
		description: 'Text for the name of `My Templates` category',
	},
	allTemplatesCountDisplay: {
		id: 'template-list.template.count.all',
		defaultMessage:
			'{totalTemplatesCount, plural, one {Displaying the only template available.} other {Displaying all {totalTemplatesCount} templates.}}',
		description: 'Display used to show the user the total template count',
	},
	filteredTemplatesCountDisplay: {
		id: 'template-list.template.count.filtered',
		defaultMessage:
			'{totalTemplatesCount, plural, one {Displaying {filteredTemplatesCount} of {totalTemplatesCount} template.} other {Displaying {filteredTemplatesCount} of {totalTemplatesCount} templates.}}',
		description: 'Display used to show the user the filtered template count',
	},
});

const DEFAULT_TEMPLATES_PAGE_SIZE = 25;

const TemplateListBase: FunctionComponent<TemplateListProps & WrappedComponentProps> =
	withErrorBoundary({
		attribution: Attribution.PAGE_MODES,
	})(({
		intl,
		onTemplateSelect,
		spaceKey: selectedSpaceKey,
		currentSpaceKey,
		searchQuery,
		selectedTemplateKey = '',
		isDisabled = false,
		templatesPageSize = DEFAULT_TEMPLATES_PAGE_SIZE,
		templateBlueprintAllowList = [],
		hasHighlightBleed,
	}) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { isLicensed, userId: currentUserId } = useSessionData();

		const useTemplateInCurrentSpace = !!currentSpaceKey;

		const trimmedSearchQuery = searchQuery ? searchQuery.trim() : undefined;

		const prevSearchQuery = useRef(trimmedSearchQuery);
		const prevSpaceKey = useRef(selectedSpaceKey);

		const prevCategoryIds = useRef<(string | null | undefined)[]>([]);

		const lastSelectedCategoryIds = useRef<(string | null | undefined)[]>([]);

		const listWrapperRef = useRef<HTMLDivElement>();

		const [selectedCategoryIds, setSelectedCategoryIds] = useState<(string | null | undefined)[]>(
			[],
		);

		const [selectedSortOrder, setSelectedSortOrder] = useState(SortOrders.RELEVANCY_SORT);

		const {
			shouldIncludeEditorTutorialTemplate,
			userContentError,
			editorOnboardingError,
			privilegeError,
			isProductOrSiteAdmin,
		} = useIncludeEditorTutorialTemplate();
		const { data: userData, error: userDataError } = useQueryInterop<TemplateListUserQuery$data>(
			graphql`
				query TemplateListUserQuery {
					user: confluence_atlassianUser {
						id
						team
					}
				}
			`,
			{
				skip: !isProductOrSiteAdmin,
			},
		);

		const teamType = userData?.user?.team;

		const {
			data: selectedSpaceData,
			loading: selectedSpaceLoading,
			error: selectedSpaceError,
		} = useQueryInterop<TemplateListQuery$data, TemplateListQuery$variables>(
			graphql`
				query TemplateListQuery(
					$spaceKey: String!
					$sortingScheme: String
					$isLicensed: Boolean!
					$teamType: String
				) @allowHigherTimeout {
					allTemplates(spaceKey: $spaceKey, sortingScheme: $sortingScheme, teamType: $teamType) {
						nodes {
							templateId
							contentBlueprintId
							blueprintModuleCompleteKey
							itemModuleCompleteKey
							templateType
							name
							description
							iconURL
							darkModeIconURL
							styleClass
							hasWizard
							isNew
							isPromoted
							isFavourite
							hasGlobalBlueprintContent
							categoryIds
							keywords
							recommendationRank
							author
						}
						pageInfo {
							hasNextPage
						}
					}
					userPreferences @include(if: $isLicensed) {
						favouriteTemplateEntityIds
					}
				}
			`,
			{
				errorPolicy: 'all',
				variables: {
					spaceKey: selectedSpaceKey,
					sortingScheme: selectedSortOrder,
					isLicensed,
					teamType,
				},
			},
		);

		const {
			data: currentSpaceData,
			loading: currentSpaceLoading,
			error: currentSpaceError,
		} = useQueryInterop<TemplateListQuery$data, TemplateListQuery$variables>(
			graphql`
				query TemplateListCurrentSpaceQuery(
					$spaceKey: String!
					$sortingScheme: String
					$isLicensed: Boolean!
					$teamType: String
				) @allowHigherTimeout {
					allTemplates(spaceKey: $spaceKey, sortingScheme: $sortingScheme, teamType: $teamType) {
						nodes {
							templateId
							contentBlueprintId
							blueprintModuleCompleteKey
							itemModuleCompleteKey
							templateType
							name
							description
							iconURL
							darkModeIconURL
							styleClass
							hasWizard
							isNew
							isPromoted
							isFavourite
							hasGlobalBlueprintContent
							categoryIds
							keywords
							recommendationRank
							author
						}
						pageInfo {
							hasNextPage
						}
					}
					userPreferences @include(if: $isLicensed) {
						favouriteTemplateEntityIds
					}
				}
			`,
			{
				errorPolicy: 'all',
				variables: {
					spaceKey: currentSpaceKey!,
					sortingScheme: selectedSortOrder,
					isLicensed,
					teamType,
				},
				skip: !useTemplateInCurrentSpace,
			},
		);

		const {
			data: categoriesData,
			loading: categoriesLoading,
			error: categoriesError,
		} = useQueryInterop<
			TemplateListTemplateCategoriesQuery$data,
			TemplateListTemplateCategoriesQuery$variables
		>(
			graphql`
				query TemplateListTemplateCategoriesQuery($spaceKey: String!) {
					templateCategories(spaceKey: $spaceKey) {
						nodes {
							id
							name
						}
					}
				}
			`,
			{
				errorPolicy: 'all',
				variables: { spaceKey: selectedSpaceKey },
			},
		);

		const hasTemplatesData =
			!!selectedSpaceData && (!useTemplateInCurrentSpace || !!currentSpaceData);
		const hasTemplatesLoading = selectedSpaceLoading || currentSpaceLoading;
		const hasTemplatesError = !!selectedSpaceError || !!currentSpaceError;
		const categories = categoriesData?.templateCategories?.nodes || [];
		let templates = selectedSpaceData?.allTemplates?.nodes || [];
		const hasNextPage = selectedSpaceData?.allTemplates?.pageInfo?.hasNextPage || false;
		const initialFavouritedTemplatesList =
			selectedSpaceData?.userPreferences?.favouriteTemplateEntityIds || [];

		if (
			useTemplateInCurrentSpace &&
			!hasTemplatesLoading &&
			!(selectedSpaceError && templates.length === 0) &&
			!currentSpaceError &&
			selectedSpaceKey !== currentSpaceKey
		) {
			const currentTemplates = currentSpaceData?.allTemplates?.nodes || [];
			templates = mergeCurrentAndSelectedTemplates(templates, currentTemplates, intl.locale);
		}

		let templatesForSearchQuery = filterEditorTutorialTemplate(
			templates,
			shouldIncludeEditorTutorialTemplate,
			selectedSortOrder,
		);

		templatesForSearchQuery = filterTemplatesByAllowList(
			templatesForSearchQuery,
			templateBlueprintAllowList,
		);

		// If the user is a new user in the onboarding state/has published <2 pages/the onboarding state is
		// not loading, then they will see the editor tutorial template. Otherwise it will be filtered out, so
		// we need to show the correct total template count.
		const totalSpaceTemplatesCount =
			shouldIncludeEditorTutorialTemplate && templateBlueprintAllowList.length === 0
				? templates.length
				: templatesForSearchQuery.length;

		templatesForSearchQuery = filterTemplatesBySearchQuery(
			templatesForSearchQuery,
			intl,
			currentUserId,
			trimmedSearchQuery,
		) as Template[];

		const renderedCategoryIds = selectedCategoryIds;

		// has to go after the filter so that it knows which templates to count
		// it doesn't apply to rendered templates for some reason if moved before the search query filter
		if (!hasTemplatesLoading) {
			// Update the templatelist using userpref as source of truth
			// The fields related to starring may be stale for global templates and blueprints
			// as cache is space level and not global for templates and blueprints
			templatesForSearchQuery = updateFavouriteTemplates(
				templatesForSearchQuery,
				initialFavouritedTemplatesList,
			);
		}

		// filtering by category is done right here and not in
		// useEffect(...) and with setSelectedCategory(...) in order
		// to achieve simultaneous update when both query and category
		// filters applied. In contrast, using setSelectedCategory(...)
		// in useEffect(..., [searchQuery]) will lead to subsequent rerender.
		const renderedCategories = getCountedCategories(templatesForSearchQuery, categories);

		const isCategoryPresent = (categoryId) => {
			return renderedCategories.find((category) => category.id == categoryId);
		};

		if (templatesForSearchQuery.length) {
			const featuredCategoryCount = getFeaturedCategoryCount(templatesForSearchQuery);

			// We either show featured pill if there are any, or we show empty state to admins
			if (
				(featuredCategoryCount || isProductOrSiteAdmin) &&
				!isCategoryPresent(FEATURED_CATEGORY_ID)
			) {
				renderedCategories.unshift({
					id: FEATURED_CATEGORY_ID,
					name: intl.formatMessage(i18n.featuredCategoryName),
					count: featuredCategoryCount,
				});
			}

			if (!isCategoryPresent(MY_TEMPLATES_CATEGORY_ID)) {
				renderedCategories.unshift({
					id: MY_TEMPLATES_CATEGORY_ID,
					name: intl.formatMessage(i18n.myTemplatesCategoryName),
					count: getMyTemplatesCategoryCount(templatesForSearchQuery, currentUserId || '0'),
				});
			}
		}

		const renderedTemplates = filterTemplatesByCategoryIds(
			templatesForSearchQuery,
			renderedCategoryIds,
			selectedSortOrder,
			currentUserId || '0',
		) as Template[];

		useEffect(() => {
			requireStyleClassResourcesForTemplates();
		}, []);

		useEffect(() => {
			if (!prevSearchQuery.current && trimmedSearchQuery) {
				lastSelectedCategoryIds.current = selectedCategoryIds;

				if (selectedCategoryIds.length) {
					setSelectedCategoryIds([]);
				}
			}

			if (!trimmedSearchQuery && prevSearchQuery.current) {
				setSelectedCategoryIds(lastSelectedCategoryIds.current);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [trimmedSearchQuery]);

		useEffect(() => {
			if (hasTemplatesLoading || hasTemplatesError) {
				return;
			}

			// Maps out a new array of categoryIds so that when the space is changed and a previously selected
			// category no longer exists in the new space, the template list can rerender and show templates that belong to
			// selected categories that exist in the new space.
			const existingCategoryIds = getExistingCategoryIds(renderedCategories, renderedCategoryIds);

			const hasQueryChanged = prevSearchQuery.current !== trimmedSearchQuery;
			const hasSpaceChanged = prevSpaceKey.current !== selectedSpaceKey;
			const haveCategoriesChanged =
				!isEqual(prevCategoryIds.current, renderedCategoryIds) ||
				!isEqual(prevCategoryIds.current, existingCategoryIds);

			if (hasSpaceChanged) {
				setSelectedCategoryIds(existingCategoryIds);
			}

			if (hasQueryChanged || hasSpaceChanged || haveCategoriesChanged) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'templateDrawer',
						action: 'updated',
						actionSubject: 'templateDrawer',
						actionSubjectId: 'searchResultUpdated',
						attributes: {
							hasQueryChanged,
							hasSpaceChanged,
							haveCategoriesChanged,
						},
					},
				}).fire();
			}

			prevSearchQuery.current = trimmedSearchQuery;
			prevSpaceKey.current = selectedSpaceKey;
			prevCategoryIds.current = renderedCategoryIds;
		}, [
			hasTemplatesLoading,
			hasTemplatesError,
			trimmedSearchQuery,
			selectedSpaceKey,
			setSelectedCategoryIds,
			createAnalyticsEvent,
			renderedTemplates,
			renderedCategories,
			renderedCategoryIds,
		]);

		const handleCategorySelected = useCallback(
			(category: CountedCategory) => {
				let selectedCategoryIds: (string | null | undefined)[] = prevCategoryIds.current;
				setSelectedCategoryIds((previousCategories) => {
					// Filter previousCategories to exclude the current category's id
					const updatedCategories = previousCategories.filter((id) => id !== category.id);

					// If the current category's id is in previousCategories, return the filtered categories.
					// Otherwise, add the current category's id to the start of the filtered categories and return that.
					selectedCategoryIds = previousCategories.includes(category.id)
						? updatedCategories
						: [category.id, ...updatedCategories];

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'templateDrawer',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'templateCategory',
							attributes: {
								selectedCategoryIds,
							},
						},
					}).fire();

					return selectedCategoryIds;
				});

				if (listWrapperRef.current) {
					listWrapperRef.current.scrollTop = 0;
				}
			},
			[createAnalyticsEvent],
		);

		const templateListOnTemplateSelect = useCallback(
			(template: Template, additionalStatAttributes: object) => {
				onTemplateSelect(template, {
					...additionalStatAttributes,
					...{ selectedCategoryIds },
					sortingScheme: selectedSortOrder,
				});
			},
			[onTemplateSelect, selectedSortOrder, selectedCategoryIds],
		);

		const onStarClick = useMemo(() => {
			return updateFavouriteTemplatesAllTemplatesQuery(
				selectedSpaceKey,
				selectedSortOrder,
				isLicensed,
				teamType,
			);
		}, [selectedSpaceKey, isLicensed, selectedSortOrder, teamType]);

		if (hasTemplatesLoading) {
			return (
				<TemplateListContainer>
					<CategoriesWrapper>
						<CategorySelector
							categories={renderedCategories}
							selectedCategoryIds={renderedCategoryIds}
							isDisabled={isDisabled}
							onCategorySelected={handleCategorySelected}
							isLoading={categoriesLoading}
						/>
					</CategoriesWrapper>
					<SortByWrapper>
						<LoadingSortByDropdown />
					</SortByWrapper>
					<LoadingTemplateListCards />
				</TemplateListContainer>
			);
		}

		if (selectedSpaceError && templates.length === 0) {
			return (
				<Fragment>
					<ErrorDisplay error={selectedSpaceError}>
						<ErrorTemplateList
							selectedSpaceError={selectedSpaceError}
							categoriesError={categoriesError}
						/>
					</ErrorDisplay>
					{categoriesError && <ErrorDisplay error={categoriesError} />}
					{currentSpaceError && <ErrorDisplay error={currentSpaceError} />}
					{userContentError && <ErrorDisplay error={userContentError} />}
					{userDataError && <ErrorDisplay error={userDataError} />}
					{privilegeError && <ErrorDisplay error={privilegeError} />}
					{editorOnboardingError && <ErrorDisplay error={editorOnboardingError} />}
				</Fragment>
			);
		}

		if (templates.length === 0) {
			return (
				<Fragment>
					<EmptyTemplateList />
					<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE} />
					<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE} />
				</Fragment>
			);
		}

		if (templatesForSearchQuery.length === 0) {
			return (
				<Fragment>
					<NoSearchResultsTemplateList />
					<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE} />
					<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE} />
				</Fragment>
			);
		}

		const handleEmptyStates = () => {
			const hasNoFeaturedTemplates =
				selectedCategoryIds.includes(FEATURED_CATEGORY_ID) && !renderedTemplates.length;

			const hasNoMyTemplates =
				selectedCategoryIds.includes(MY_TEMPLATES_CATEGORY_ID) && !renderedTemplates.length;

			if (hasNoMyTemplates) {
				return <EmptyMyTemplates />;
			}

			if (hasNoFeaturedTemplates) {
				return <EmptyFeaturedTemplates spaceKey={selectedSpaceKey} />;
			}

			return null;
		};

		const shouldUpdateTemplateCount = selectedCategoryIds.length > 0 || searchQuery;

		const templateCountDisplay = () => {
			// allTemplates sends up to 500 templates at a time from the BE. After 500 templates, it only sends
			// more templates as the user scrolls (paginated) so if there are more than 500 templates, we want to show
			// 500+ templates to the user.
			const isOver500TotalTemplates = templates.length > 500 && hasNextPage;
			const totalTemplatesCount = isOver500TotalTemplates ? '500+' : totalSpaceTemplatesCount;
			const filteredTemplatesCount = renderedTemplates.length;

			const totalTemplatesCountMessage = intl.formatMessage(i18n.allTemplatesCountDisplay, {
				totalTemplatesCount: <b>{totalTemplatesCount}</b>,
			});
			const filteredTemplatesCountMessage = intl.formatMessage(i18n.filteredTemplatesCountDisplay, {
				filteredTemplatesCount: <b>{filteredTemplatesCount}</b>,
				totalTemplatesCount,
			});

			return (
				<CountDisplayWrapper data-testid="count-display">
					{shouldUpdateTemplateCount ? filteredTemplatesCountMessage : totalTemplatesCountMessage}
				</CountDisplayWrapper>
			);
		};

		const isNotLoadingAnything =
			!currentSpaceLoading && !selectedSpaceLoading && !categoriesLoading;

		return (
			<TemplateListContainer>
				{selectedSpaceError && (
					<Fragment>
						<ErrorDisplay error={selectedSpaceError} />
						<ExperienceFailure
							name={IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE}
							error={selectedSpaceError}
						/>
					</Fragment>
				)}
				{categoriesError && (
					<Fragment>
						<ErrorDisplay error={categoriesError} />
						<ExperienceFailure
							name={IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE}
							error={categoriesError}
						/>
					</Fragment>
				)}
				{currentSpaceError && (
					<Fragment>
						<ErrorDisplay error={currentSpaceError} />
						<ExperienceFailure
							name={IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE}
							error={currentSpaceError}
						/>
					</Fragment>
				)}
				{userContentError && <ErrorDisplay error={userContentError} />}
				{userDataError && <ErrorDisplay error={userDataError} />}
				{privilegeError && <ErrorDisplay error={privilegeError} />}
				{editorOnboardingError && <ErrorDisplay error={editorOnboardingError} />}
				{hasTemplatesData && <PageSegmentLoadEnd metricKey={IN_EDITOR_TEMPLATES_SEGMENT_KEY} />}
				{!!renderedCategories.length && (
					<CategoriesWrapper>
						<CategorySelector
							categories={renderedCategories}
							selectedCategoryIds={renderedCategoryIds}
							isDisabled={isDisabled}
							isLoading={categoriesLoading}
							onCategorySelected={handleCategorySelected}
						/>
					</CategoriesWrapper>
				)}
				{handleEmptyStates() || (
					<Fragment>
						<SortByWrapper>
							<SortByDropdown
								onChange={setSelectedSortOrder}
								value={selectedSortOrder}
								isDisabled={isDisabled}
								analyticsSource="templateDrawer"
							/>
						</SortByWrapper>
						{isNotLoadingAnything && templateCountDisplay()}
						<ListWrapper
							ref={listWrapperRef as RefObject<HTMLDivElement>}
							hasBleed={hasHighlightBleed}
						>
							<PopupPreview
								spaceKey={selectedSpaceKey}
								templates={renderedTemplates}
								searchQuery={trimmedSearchQuery}
								hoverSource="templateDrawer"
								renderedCategoryIds={renderedCategoryIds}
							>
								{({ showPreview, hidePreview }) => (
									<TemplateListComponent
										onTemplateSelect={templateListOnTemplateSelect}
										templates={renderedTemplates}
										selectedTemplateKey={selectedTemplateKey}
										isDisabled={isDisabled}
										onStarClick={onStarClick}
										onTemplateMouseEnter={showPreview}
										onTemplateListMouseLeave={hidePreview}
										templatesPageSize={templatesPageSize}
										hasHighlightBleed={hasHighlightBleed}
									/>
								)}
							</PopupPreview>
						</ListWrapper>
					</Fragment>
				)}
			</TemplateListContainer>
		);
	});

export const TemplateList = injectIntl(TemplateListBase);
